import { Tabs } from 'antd';
import React, { memo, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import messages from '../../containers/OrdersPage/messages';
import { OrderStatus } from '../../types/schema';

const StyledTabs = styled(Tabs)`
  margin-bottom: 16px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.05);
  padding-top: 16px;

  .ant-tabs-nav {
    margin: 0;
    padding: 0 16px;
  }

  .ant-tabs-tab {
    font-size: 16px;
    line-height: 24px;
    color: #585a60;
    font-weight: 400;
    padding: 16px 0 12px;

    &:hover {
      color: #43a047;
    }

    &.ant-tabs-tab-active .ant-tabs-tab-btn {
      color: #43a047;
      font-weight: 500;
    }
  }

  .ant-tabs-ink-bar {
    background-color: #43a047;
    height: 3px;
  }

  .ant-tabs-nav-wrap {
    overflow-x: auto;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

interface TabCounts {
  pending: number | null;
  waiting: number | null;
}

interface FilterStatusProps {
  statuses: string | string[];
  onChangeTab: (key: string) => void;
  tabCounts?: TabCounts;
}

type TabKey = 'all' | 'pending' | 'waiting' | 'delivered' | 'cancelled';

const FilterStatusComponent = ({ statuses, onChangeTab, tabCounts }: FilterStatusProps) => {
  const activeKey = useMemo((): TabKey => {
    if (!statuses || statuses.length === 0) {
      return 'all';
    }

    const statusValue = Array.isArray(statuses) ? statuses : [statuses];

    const hasPending = statusValue.some((status) =>
      [OrderStatus.Sent, OrderStatus.Accepted, OrderStatus.InProgress, OrderStatus.PendingApproval].includes(
        status as OrderStatus,
      ),
    );
    if (hasPending) {
      return 'pending';
    }

    const hasWaiting = statusValue.some((status) =>
      [OrderStatus.PendingDelivery, OrderStatus.Delivering].includes(status as OrderStatus),
    );
    if (hasWaiting) {
      return 'waiting';
    }

    const hasDelivered = statusValue.some((status) => status === OrderStatus.Delivered);
    if (hasDelivered) {
      return 'delivered';
    }
    const hasCancelled = statusValue.some((status) =>
      [OrderStatus.Canceled, OrderStatus.Declined, OrderStatus.ApprovalDisapproved].includes(status as OrderStatus),
    );
    if (hasCancelled) {
      return 'cancelled';
    }

    return 'all';
  }, [statuses]);

  const tabItems = useMemo(
    () => [
      {
        key: 'all',
        label: <FormattedMessage {...messages.allStatus} />,
      },
      {
        key: 'pending',
        label: (
          <>
            <FormattedMessage {...messages.pendingStatus} />
            {!!tabCounts?.pending && <span style={{ marginLeft: '4px' }}>({tabCounts.pending})</span>}
          </>
        ),
      },
      {
        key: 'waiting',
        label: (
          <>
            <FormattedMessage {...messages.waitingDeliveryStatus} />
            {!!tabCounts?.waiting && <span style={{ marginLeft: '4px' }}>({tabCounts.waiting})</span>}
          </>
        ),
      },
      {
        key: 'delivered',
        label: <FormattedMessage {...messages.deliveredStatus} />,
      },
      {
        key: 'cancelled',
        label: <FormattedMessage {...messages.cancelledStatus} />,
      },
    ],
    [tabCounts],
  );

  return <StyledTabs activeKey={activeKey} onChange={onChangeTab} items={tabItems} />;
};

export const getStatusesByTabKey = (key: string): string[] | undefined => {
  if (key === 'all') return undefined;
  if (key === 'pending') {
    return [OrderStatus.Sent, OrderStatus.Accepted, OrderStatus.InProgress, OrderStatus.PendingApproval];
  }
  if (key === 'waiting') return [OrderStatus.PendingDelivery, OrderStatus.Delivering];
  if (key === 'delivered') return [OrderStatus.Delivered];
  if (key === 'cancelled') return [OrderStatus.Canceled, OrderStatus.Declined, OrderStatus.ApprovalDisapproved];
  return undefined;
};

const FilterStatus = memo(FilterStatusComponent);

export default FilterStatus;
